import React, { useContext, useRef, useMemo } from 'react';
import { useAsync } from 'react-commons';
import { Button } from 'react-bootstrap';
import { Column, TableInstance } from 'react-table';
import { deleteDelegate, getDelegates } from '../../../api/delegatesApi';
import { DelegatesType } from '../../../api/delegatesApiTypes';
import ConfigContext from '../../../contexts/ConfigContext';
import Spinner from '../../common/Spinner';
import ErrorBlock from '../../common/ErrorBlock';
import { FormType } from '../../common/types/formTypes';
import {
    renderDateCell,
    formatDate,
    sendToast,
    formatExportText,
    formatBooleanAsYesNo,
} from '../../../utils/displayUtils';
import DelegatesViewModal from './modal/DelegatesViewModal';
import DelegatesModal from './modal/DelegatesModal';
import DeleteConfirmationModal from '../../common/modal/DeleteConfirmationModal';
import NinModal from '../../common/modal/NinModal';
import DataTable from '../../common/datatables/DataTable';
import { EXPORT_DATE_FORMAT, DEFAULT_ADMIN_INITIAL_STATE } from '../../../utils/Constants';
import { generateCSV } from '../../common/listUtils';
import useStateWithSessionStorage from '../../../hooks/useStateWithSessionStorage';

const Delegates = () => {
    const config = useContext(ConfigContext);

    const [initialState, setInitialState] = useStateWithSessionStorage(
        'delegatesInitialState',
        JSON.stringify(DEFAULT_ADMIN_INITIAL_STATE),
    );

    const defaultState: Array<DelegatesType> = [];
    const {
        value: delegates,
        error,
        loading,
        refresh,
    } = useAsync<Array<DelegatesType>>(() => getDelegates(config), defaultState);

    const tableInstanceRef = useRef<TableInstance<DelegatesType> | null>(null);

    if (error) {
        return typeof error === 'object' ? (
            <ErrorBlock errorMessage="Network Error!" />
        ) : (
            <ErrorBlock errorMessage={error} />
        );
    }

    const delegateOptions = (delegate: DelegatesType) => (
        <div className="btn-group options-icon">
            <button
                type="button"
                className="btn btn-link p-0"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
            >
                <span className="fas fa-ellipsis-h text-muted" />
            </button>

            <ul className="dropdown-menu dropdown-menu-right">
                <li>
                    <NinModal
                        button={
                            <Button
                                variant="button"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                View Delegate
                            </Button>
                        }
                        render={(toggleModal: () => void) => (
                            <DelegatesViewModal delegate={delegate} toggleModal={toggleModal} />
                        )}
                    />
                </li>
                <li role="separator" className="dropdown-divider" />
                <li>
                    <DelegatesModal
                        title="Edit Delegate"
                        toggleButton={
                            <Button
                                variant="button"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                Edit Delegate
                            </Button>
                        }
                        formType={FormType.UPDATE}
                        delegate={delegate}
                        onSubmit={refresh}
                    />
                </li>
                <li>
                    <DelegatesModal
                        title="Copy Delegate"
                        toggleButton={
                            <Button
                                variant="button"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                Copy Delegate
                            </Button>
                        }
                        formType={FormType.CLONE}
                        delegate={delegate}
                        onSubmit={refresh}
                    />
                </li>
                {delegate.id && (
                    <DeleteConfirmationModal
                        title="Delete Delegate"
                        onConfirm={async () => {
                            if (delegate.id) {
                                await deleteDelegate(config, delegate.id);
                                sendToast(
                                    <>
                                        Deleted Delegate: <strong>{delegate.personName}</strong>
                                    </>,
                                );
                                refresh();
                            }
                        }}
                        body="Please confirm that you would like to delete this delegate. "
                        toggleButton={
                            <Button
                                variant="button"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span className="text-danger">Delete Delegate</span>
                            </Button>
                        }
                        confirmButtonText="Confirm Delete"
                    />
                )}
            </ul>
        </div>
    );

    const columns = [
        {
            Header: '',
            accessor: 'active',
            Cell: (cellProps: { value: boolean }) =>
                useMemo(
                    () => (
                        <div className="text-center">
                            {cellProps.value ? (
                                <i className="fas fa-check-circle text-success" />
                            ) : (
                                ''
                            )}
                        </div>
                    ),
                    [],
                ),
            disableSortBy: true,
            width: 50,
        },
        {
            Header: 'Name',
            accessor: 'personName',
            width: null,
        },
        {
            Header: 'Delegate',
            accessor: 'proxyName',
            Cell: (cellProps: { value: string }) =>
                useMemo(() => <span>{cellProps.value ? cellProps.value : '--'}</span>, []),
            width: null,
        },
        {
            Header: 'Start Date',
            accessor: 'startDate',
            width: 200,
            Cell: renderDateCell,
        },
        {
            Header: 'End Date',
            accessor: 'endDate',
            width: 200,
            Cell: renderDateCell,
        },
        {
            id: 'options',
            Header: '',
            className: 'table-cell-pointer overflow-visible',
            Cell: (cellProps: { row: { original: DelegatesType } }) =>
                delegateOptions(cellProps.row.original),
            disableSortBy: true,
            width: 55,
        },
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">Delegates</h3>
                    <div className="pull-right">
                        <DelegatesModal
                            title="Create Delegate "
                            toggleButton={
                                <Button type="button" className="btn btn-primary">
                                    <i className="fas fa-plus mr-2" /> Create Delegate
                                </Button>
                            }
                            formType={FormType.CREATE}
                            onSubmit={refresh}
                        />
                        <Button
                            variant="outline-primary"
                            className="ml-1"
                            disabled={loading || delegates.length <= 0}
                            onClick={() => {
                                if (tableInstanceRef.current) {
                                    const list = tableInstanceRef.current.rows
                                        .map((row) => row.original)
                                        .map((delegate) => ({
                                            Active: formatBooleanAsYesNo(delegate.active),
                                            Person: formatExportText(delegate.personName),
                                            Delegate: formatExportText(delegate.proxyName),
                                            Description: formatExportText(delegate.description),
                                            'Start Date': delegate.startDate
                                                ? formatDate(delegate.startDate, EXPORT_DATE_FORMAT)
                                                : '',
                                            'End Date': delegate.endDate
                                                ? formatDate(delegate.endDate, EXPORT_DATE_FORMAT)
                                                : '',
                                        }));
                                    generateCSV(list, 'Delegates.csv');
                                }
                            }}
                        >
                            <i className="fas fa-file-export" /> Export CSV
                        </Button>
                    </div>
                </div>
            </div>

            <div className="card border-top">
                <div className="card-body">
                    {!loading ? (
                        <DataTable<DelegatesType>
                            columns={columns as Column<DelegatesType>[]}
                            data={delegates}
                            initialState={JSON.parse(initialState)}
                            setInitialState={setInitialState}
                            noGlobalFilter
                            getInstanceCallback={(tableInstance) => {
                                if (tableInstanceRef) {
                                    tableInstanceRef.current = tableInstance;
                                }
                            }}
                        />
                    ) : (
                        <Spinner />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Delegates;
